/**
 * React Query hooks for resilience drills (P132, #482).
 *
 * Mirrors backend/twin/drills_router.py.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { drillKeys } from "shared/api/queryClient";
import type {
  DrillAction,
  DrillIbsOption,
  DrillIn,
  ResilienceDrill,
} from "shared/api/types";

export function useDrills(ibsId?: string): UseQueryResult<ResilienceDrill[], ApiError> {
  return useQuery({
    queryKey: drillKeys.list(ibsId),
    queryFn: () => {
      const q = ibsId ? `?ibs_service_id=${encodeURIComponent(ibsId)}` : "";
      return api.get<ResilienceDrill[]>(`/v1/drills${q}`);
    },
  });
}

export function useDrillIbsOptions(
  enabled: boolean,
): UseQueryResult<DrillIbsOption[], ApiError> {
  return useQuery({
    queryKey: [...drillKeys.all, "ibs-options"],
    queryFn: () => api.get<DrillIbsOption[]>("/v1/drills-ibs-options"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreateDrill(): UseMutationResult<
  ResilienceDrill,
  ApiError,
  DrillIn
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) => api.post<ResilienceDrill>("/v1/admin/twin/drills", body),
    // Round 17 G17 -- the new row at once, and the refetch NOT awaited:
    // awaiting it held the caller's "Drill scheduled." until every drill
    // query had refetched, which on a busy host read as nothing having
    // happened.
    onSuccess: (created) => {
      qc.setQueryData<ResilienceDrill[]>(drillKeys.list(undefined), (old) =>
        Array.isArray(old) && !old.some((d) => d.id === created.id)
          ? [created, ...old]
          : old,
      );
      void qc.invalidateQueries({ queryKey: drillKeys.all });
    },
  });
}

export function useTransitionDrill(): UseMutationResult<
  ResilienceDrill,
  ApiError,
  { id: string; action: DrillAction; severity?: string; outcomeNotes?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ id, action, severity, outcomeNotes }) =>
      api.post<ResilienceDrill>(`/v1/admin/twin/drills/${id}/transitions`, {
        action,
        ...(severity !== undefined ? { severity } : {}),
        ...(outcomeNotes !== undefined ? { outcome_notes: outcomeNotes } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: drillKeys.all }),
  });
}
